import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography, Divider } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormContainer,
  MultiSelectElement,
  TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getQuestionById,
  getQuestionCategories,
} from '../../Services/httpCalls';
import useAuth from '../../Services/useAuth';
import { useGetActiveGroups } from '../../Services/useGetActiveGroups';
import { usePostNewQuestion } from '../../Services/usePostNewQuestion';
import { usePutAnswer } from '../../Services/usePutAnswer';
import RoundedButton from '../../UIComponents/Buttons/RoundedButton';
import GovWrapper from '../../UIComponents/GovWrapper';
import Loader from '../../UIComponents/Loader';
import { newQuestionDefaultValues } from '../../helperFuncs';
import { answerValidation, questionValidation } from './questionValidation';
import { useWindowSize } from '@uidotdev/usehooks';
import GovButton from '../../UIComponents/Buttons/GovButton';

const NewQuestion = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const identifier = searchParams.get('identifier');
  const [isAnswered, setIsAnswered] = useState();
  const [formData, setFormData] = useState();
  const [isReseting, setIsReseting] = useState(false);
  const { getRole, getUser } = useAuth();
  const [questionData, setQuestionData] = useState({
    first_name: '',
    last_name: '',
    creation_date: '',
  });
  const [answerFrom, setAnswerFrom] = useState('');
  const [categories, setCategories] = useState([]);

  const navigate = useNavigate();
  const {
    mutateAsync: postQuestion,
    isSuccess: isQuestionSuccess,
    isError: isQuestionError,
  } = usePostNewQuestion(formData);
  const { mutateAsync: updateAnswer, isSuccess: isAnswerSuccess } =
    usePutAnswer({
      answer: formData?.answer,
      question_id: id,
    });
  const { data: questionGroups, isLoading } = useGetActiveGroups();
  const DELETE_ACTION_IDENTIFIER = 'delete';

  const formValidation =
    getRole() === 'admin' ? answerValidation : questionValidation;

  const formContext = useForm({
    resolver: yupResolver(formValidation),
    defaultValues: newQuestionDefaultValues,
  });

  const fetchCategories = useCallback(async () => {
    const res = await getQuestionCategories({ is_active: 1 });

    const refactoredCategories = res.categories.map((category) => ({
      id: category.id,
      label: category.description,
    }));
    setCategories(refactoredCategories);

    if (identifier === DELETE_ACTION_IDENTIFIER) {
      const deleteIdentifierRow = res.categories.find(
        (row) => row.identifier === DELETE_ACTION_IDENTIFIER,
      );

      if (deleteIdentifierRow?.id) {
        formContext.reset({
          category_ids: [deleteIdentifierRow?.id],
        });
      }
    }
  }, [identifier, formContext]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const mappedGroups = questionGroups?.data?.categories?.map((item) => ({
    id: item.id,
    label: item.category,
  }));

  const afterSuccessfullSubmit = (message, redirectPath) => {
    enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 2000,
    });
    navigate(redirectPath);
  };

  const handleClick = async (data) => {
    try {
      const { answer, ...questionData } = data;
      const role = getRole();

      if (role === 'admin') {
        setFormData(data);
        await updateAnswer();
        afterSuccessfullSubmit('Η απάντηση στάλθηκε', '/questions-management');
      } else {
        if (questionData.category_ids.length === 0) {
          enqueueSnackbar('Πρέπει να επιλέξετε τουλάχιστον μια κατηγορία', {
            variant: 'error',
            autoHideDuration: 3000,
          });
          return;
        }
        setFormData(questionData);
        await postQuestion();
        afterSuccessfullSubmit('Το ερώτημά σας στάλθηκε', '/questions');
      }
    } catch (error) {
      enqueueSnackbar('Παρουσιάστηκε κάποιο σφάλμα', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  useEffect(() => {
    const fetchQuestion = async () => {
      setIsReseting(true);
      const resp = await getQuestionById(id);
      const question = resp?.data?.questions[0];
      setQuestionData({
        creation_date: question?.creation_date,
        first_name: question?.first_name,
        last_name: question?.last_name,
        afm: question?.afm,
      });

      setIsAnswered(question?.answer);

      if (
        question?.answer_user_id &&
        question.answer_first_name &&
        question.answer_last_name
      ) {
        setAnswerFrom(
          `${question.answer_first_name} ${question.answer_last_name}`,
        );
      }
      formContext.reset({
        subject: question?.subject,
        category_ids: question?.question_categories?.map(
          (item) => item.category_id,
        ),
        description: question?.description,
        answer: question?.answer,
      });
      setIsReseting(false);
    };

    id && fetchQuestion();
  }, [id]);

  const TitleQuestion = () => {
    return <Typography variant="h2">Προτάσεις/Ερωτήσεις</Typography>;
  };

  const DetailRow = ({ label, value }) => (
    <>
      <Box
        display="grid"
        gridTemplateColumns={'400px 1fr'}
        alignItems="center"
        my={1}
      >
        <Typography fontWeight={600}>{label}</Typography>
        <Typography>{value}</Typography>
      </Box>
      <Divider />
    </>
  );

  const SubtitleQuestion = () => {
    if (!id) {
      return (
        <Typography>
          Παρακαλούμε πολύ στείλτε μας τις προτάσεις/ερωτήσεις σας για τη
          βελτίωση του Μητρώου Απασχολούμενων στον Πολιτισμό.
        </Typography>
      );
    } else if (id && getRole() === 'admin') {
      return (
        <Box>
          <Typography mb={2}>
            Διαχειριστείτε και απαντήστε στα ερωτήματα των εξωτερικών χρηστών
            σχετικά με το Mητρώο Απασχολούμενων στον Πολιτιστικό και Δημιουργικό
            Τομέα.
          </Typography>

          <Typography mb={2}>
            Τα στοιχεία του χρήστη που υπέβαλε το ερώτημα:{' '}
          </Typography>

          <DetailRow label={'Oνομα:'} value={questionData?.first_name} />
          <DetailRow label={'Επώνυμο:'} value={questionData?.last_name} />
          <DetailRow label={'ΑΦΜ:'} value={questionData?.afm} />
          <DetailRow
            label={'Ημερομηνία υποβολής του ερωτήματος:'}
            value={questionData?.creation_date}
          />
        </Box>
      );
    } else {
      return <></>;
    }
  };

  const AnswerFromUser = () => {
    if (isAnswered && getRole() === 'admin') {
      return (
        <Box display="inline-flex">
          <Typography fontWeight={600} mr={2}>
            Απαντήθηκε από :{' '}
          </Typography>
          <Typography>{answerFrom}</Typography>
        </Box>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Box>
      {/* <BackButton /> */}
      {isReseting ? (
        <Loader />
      ) : (
        <FormContainer
          formContext={formContext}
          onSuccess={(data) => {
            handleClick(data);
          }}
        >
          <Box
            sx={{
              display: 'flex',
              mt: 2,
              mb: 7,
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <TitleQuestion />
            <SubtitleQuestion />
            <Box>
              <GovWrapper
                id="category_ids"
                label={id ? 'Κατηγορίες' : 'Επιλογή κατηγορίας'}
                width={'100%'}
              >
                <>
                  {mappedGroups && (
                    <MultiSelectElement
                      disabled={id}
                      inputProps={{
                        readOnly: id,
                        'aria-labelledby': 'category_ids',
                      }}
                      sx={{ color: 'black !important' }}
                      labelId="category_ids"
                      name="category_ids"
                      options={categories}
                    />
                  )}
                </>
              </GovWrapper>
              <GovWrapper label={'Θέμα :'} id="topic" width={'100%'}>
                <TextFieldElement
                  id="topic"
                  inputProps={{
                    readOnly: id,
                    'aria-labelledby': 'topic',
                  }}
                  InputProps={{
                    style: { padding: 0, borderRadius: 0, disabled: true },
                  }}
                  name="subject"
                />
              </GovWrapper>
              <GovWrapper
                id="description"
                label={'Κείμενο :'}
                aria-label="Κείμενο"
                width={'100%'}
              >
                <TextFieldElement
                  aria-multiline
                  fullWidth
                  rows="5"
                  inputProps={{
                    readOnly: id,
                    role: 'textbox',
                    'aria-labelledby': 'description',
                    'aria-label': 'Κείμενο',
                    'aria-describedby': 'description',
                  }}
                  InputProps={{
                    style: { padding: 0, borderRadius: 0 },
                  }}
                  name="description"
                  multiline
                />
              </GovWrapper>
            </Box>
          </Box>

          <AnswerFromUser />

          {(getRole() === 'admin' || isAnswered) && (
            <GovWrapper
              label={'Απάντηση :'}
              width={'100%'}
              id="answer"
              aria-label="Κείμενο"
            >
              <TextFieldElement
                multiline
                labelId="answer"
                inputProps={{
                  readOnly: getRole() === 'user',
                  'aria-labelledby': 'answer',
                }}
                InputProps={{
                  style: { padding: 0, borderRadius: 0 },
                }}
                sx={{ mb: 3 }}
                name="answer"
                rows="5"
                margin="normal"
                aria-label="answer"
              />
            </GovWrapper>
          )}
          {((getRole() === 'admin' && !isAnswered) || !id) && (
            <Box sx={{ my: 2 }}>
              <GovButton
                ariaLabel={'Υποβολή ερωτήματος'}
                type={'submit'}
                variant="contained"
                background={'#006736'}
                hoverBackground={'#154a2b'}
                borderColor={'#006736'}
              >
                Υποβολή
              </GovButton>
            </Box>
          )}
        </FormContainer>
      )}
    </Box>
  );
};

export default NewQuestion;
